import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from './useAuth'
import type { Role } from './types'
import { PageMessage } from '../components/PageMessage'

/**
 * Route guard. This only shapes the UI - the API enforces authorization
 * independently, so hiding a route is a convenience and never a control.
 */
export function RequireAuth({ children, role }: { children: ReactNode; role?: Role }) {
  const { status, can } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return <PageMessage title="Loading" body="Checking your session..." />
  }

  if (status === 'revoked') {
    return <Navigate to="/access-revoked" replace />
  }

  if (status === 'anonymous') {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  }

  if (role && !can(role)) {
    return (
      <PageMessage
        title="Not available to you"
        body={`This area needs the ${role} role. Ask an administrator if you need access.`}
      />
    )
  }

  return <>{children}</>
}
